import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import * as I from 'immutable';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

type GLTERMProps = {
  plan: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  situsState: StringUnion;
  type: StringUnion;
  faceAmt: StringUnion;
  policyNumber: PolicyNumber;
  premiumType: StringUnion;
  priInsured: StringUnion;
  spouseInsured: StringUnion;
  autoIssueEligibility: boolean;
  replacement: boolean;
  riderCodes: I.List<string>;
  riderAmounts: I.List<number>;
  commission: StringUnion;
  deathBenefit: StringUnion;
  adb: boolean;
  sbVolume: StringUnion;
  premiumWaiver: boolean;
};

export class InvalidOrdGLTERM {
  public readonly key = 'InvalidOrdGLTERM';

  constructor(
    public readonly plan: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly situsState: StringUnion,
    public readonly type: StringUnion,
    public readonly faceAmt: StringUnion,
    public readonly policyNumber: PolicyNumber,
    public readonly premiumType: StringUnion,
    public readonly priInsured: StringUnion,
    public readonly spouseInsured: StringUnion,
    public readonly autoIssueEligibility: boolean,
    public readonly replacement: boolean,
    public readonly riderCodes: I.List<string>,
    public readonly riderAmounts: I.List<number>,
    public readonly commission: StringUnion,
    public readonly deathBenefit: StringUnion,
    public readonly adb: boolean,
    public readonly sbVolume: StringUnion,
    public readonly premiumWaiver: boolean
  ) {}
}

export class ValidOrdGLTERM {
  public static createApp({
    plan,
    premium,
    calculatedPremium,
    cwa,
    situsState,
    type,
    faceAmt,
    policyNumber,
    premiumType,
    priInsured,
    spouseInsured,
    autoIssueEligibility,
    replacement,
    riderCodes,
    riderAmounts,
    commission,
    deathBenefit,
    adb,
    sbVolume,
    premiumWaiver,
  }: GLTERMProps) {
    return this.create(
      plan,
      premium,
      calculatedPremium,
      cwa,
      situsState,
      type,
      faceAmt,
      policyNumber,
      premiumType,
      priInsured,
      spouseInsured,
      autoIssueEligibility,
      replacement,
      riderCodes,
      riderAmounts,
      commission,
      deathBenefit,
      adb,
      sbVolume,
      premiumWaiver
    );
  }

  public static create(
    plan: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    situsState: StringUnion,
    type: StringUnion,
    faceAmt: StringUnion,
    policyNumber: PolicyNumber,
    premiumType: StringUnion,
    priInsured: StringUnion,
    spouseInsured: StringUnion,
    autoIssueEligibility: boolean,
    replacement: boolean,
    riderCodes: I.List<string>,
    riderAmounts: I.List<number>,
    commission: StringUnion,
    deathBenefit: StringUnion,
    adb: boolean,
    sbVolume: StringUnion,
    premiumWaiver: boolean
  ) {
    return isNonEmptyString(plan)
      ? new ValidOrdGLTERM(
          plan,
          premium,
          calculatedPremium,
          cwa,
          situsState,
          type,
          faceAmt,
          policyNumber,
          premiumType,
          priInsured,
          spouseInsured,
          autoIssueEligibility,
          replacement,
          riderCodes,
          riderAmounts,
          commission,
          deathBenefit,
          adb,
          sbVolume,
          premiumWaiver
        )
      : new InvalidOrdGLTERM(
          plan,
          premium,
          calculatedPremium,
          cwa,
          situsState,
          type,
          faceAmt,
          policyNumber,
          premiumType,
          priInsured,
          spouseInsured,
          autoIssueEligibility,
          replacement,
          riderCodes,
          riderAmounts,
          commission,
          deathBenefit,
          adb,
          sbVolume,
          premiumWaiver
        );
  }

  public readonly key = 'ValidOrdGLTERM';
  constructor(
    public readonly plan: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly situsState: StringUnion,
    public readonly type: StringUnion,
    public readonly faceAmt: StringUnion,
    public readonly policyNumber: PolicyNumber,
    public readonly premiumType: StringUnion,
    public readonly priInsured: StringUnion,
    public readonly spouseInsured: StringUnion,
    public readonly autoIssueEligibility: boolean,
    public readonly replacement: boolean,
    public readonly riderCodes: I.List<string>,
    public readonly riderAmounts: I.List<number>,
    public readonly commission: StringUnion,
    public readonly deathBenefit: StringUnion,
    public readonly adb: boolean,
    public readonly sbVolume: StringUnion,
    public readonly premiumWaiver: boolean
  ) {}
}

export type OrdGLTERM = InvalidOrdGLTERM | ValidOrdGLTERM;

export type OrdGLTERMOptions<TReturn> = ToPartialMatchOptions<OrdGLTERM, 'key', TReturn>;

export const matchOrdGLTERM = <TReturn>(options: OrdGLTERMOptions<TReturn>) => (app: OrdGLTERM): TReturn => {
  switch (app.key) {
    case 'InvalidOrdGLTERM':
      return options.InvalidOrdGLTERM ? options.InvalidOrdGLTERM(app) : options.default(app);
    case 'ValidOrdGLTERM':
      return options.ValidOrdGLTERM ? options.ValidOrdGLTERM(app) : options.default(app);
  }
};

export const EmptyOrdGLTERM = new InvalidOrdGLTERM(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  EmptyPolicyNumber,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  I.List<string>(),
  I.List<number>(),
  new EmptyString(),
  new EmptyString(),
  false,
  new EmptyString(),
  false
);

export const changePlan = (plan: string) => (app: OrdGLTERM) =>
  ValidOrdGLTERM.createApp({
    ...app,
    plan: NonEmptyString.create(plan),
    riderCodes: I.List<string>(),
    riderAmounts: I.List<number>(),
  });
export const changePremium = (premium: string) => (app: OrdGLTERM) =>
  ValidOrdGLTERM.createApp({...app, premium: NonEmptyString.create(premium)});
export const changeCalculatedPremium = (calculatedPremium: string) => (app: OrdGLTERM) =>
  ValidOrdGLTERM.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});
export const changeCWA = (cwa: string) => (app: OrdGLTERM) =>
  ValidOrdGLTERM.createApp({...app, cwa: NonEmptyString.create(cwa)});
export const changeSitusState = (situsState: string) => (app: OrdGLTERM) =>
  ValidOrdGLTERM.createApp({...app, situsState: NonEmptyString.create(situsState)});
export const changeType = (type: string) => (app: OrdGLTERM) =>
  ValidOrdGLTERM.createApp({...app, type: NonEmptyString.create(type)});
export const changeFaceAmt = (faceAmt: string) => (app: OrdGLTERM) =>
  ValidOrdGLTERM.createApp({...app, faceAmt: NonEmptyString.create(faceAmt)});
export const changePolicyNumber = (polNum: string) => (app: OrdGLTERM) =>
  ValidOrdGLTERM.createApp({...app, policyNumber: ValidPolicyNumber.create(NonEmptyString.create(polNum))});
export const changePremiumType = (preType: string) => (app: OrdGLTERM) =>
  ValidOrdGLTERM.createApp({...app, premiumType: NonEmptyString.create(preType)});
export const changePriInsured = (priInsured: string) => (app: OrdGLTERM) =>
  ValidOrdGLTERM.createApp({...app, priInsured: NonEmptyString.create(priInsured)});
export const changeSpouseInsured = (spouseInsured: string) => (app: OrdGLTERM) =>
  ValidOrdGLTERM.createApp({...app, spouseInsured: NonEmptyString.create(spouseInsured)});
export const changeAutoIssue = (autoIssueEligibility: boolean) => (app: OrdGLTERM) =>
  ValidOrdGLTERM.createApp({...app, autoIssueEligibility});
export const changeReplacement = (replacement: boolean) => (app: OrdGLTERM) =>
  ValidOrdGLTERM.createApp({...app, replacement});
export const changeCommision = (commission: string) => (app: OrdGLTERM) =>
  ValidOrdGLTERM.createApp({...app, commission: NonEmptyString.create(commission)});
export const changeDeathBenefit = (deathBenefit: string) => (app: OrdGLTERM) =>
  ValidOrdGLTERM.createApp({...app, deathBenefit: NonEmptyString.create(deathBenefit)});
export const changeSBVolume = (vol: string) => (app: OrdGLTERM) =>
  ValidOrdGLTERM.createApp({...app, sbVolume: NonEmptyString.create(vol)});
export const changeADB = (adb: boolean) => (app: OrdGLTERM) => ValidOrdGLTERM.createApp({...app, adb});
export const changeWaiver = (premiumWaiver: boolean) => (app: OrdGLTERM) =>
  ValidOrdGLTERM.createApp({...app, premiumWaiver});

export const changeRiderCodes = (riderCode: string, index: number) => (app: OrdGLTERM) => {
  const existingList: I.List<string> = app.riderCodes;
  const newList = existingList.set(index, riderCode);
  return ValidOrdGLTERM.createApp({...app, riderCodes: newList});
};

export const changeRiderAmount = (riderAmt: number, index: number) => (app: OrdGLTERM) => {
  const existingList: I.List<number> = app.riderAmounts;
  const newList = existingList.set(index, riderAmt);

  return ValidOrdGLTERM.createApp({...app, riderAmounts: newList});
};
